import {
  Directive,
  ElementRef,
  HostListener,
  PLATFORM_ID,
  inject,
  input,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Pointer-tracked 3D tilt with a moving specular glare, used on cards
 * (tour cards, testimonials, value tiles) instead of a flat hover state.
 * No-ops on touch/coarse pointers and when reduced-motion is requested.
 */
@Directive({
  selector: '[appTilt]',
  standalone: true,
})
export class Tilt {
  private el = inject(ElementRef<HTMLElement>);
  private platformId = inject(PLATFORM_ID);

  maxDeg = input<number>(8, { alias: 'appTiltMax' });

  private enabled = false;
  private glareEl: HTMLDivElement | null = null;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      this.enabled = !reduced && !coarse;

      if (this.enabled) {
        const host = this.el.nativeElement;
        host.style.transformStyle = 'preserve-3d';
        host.style.transition = 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)';
        host.style.willChange = 'transform';

        const glare = document.createElement('div');
        glare.style.cssText =
          'position:absolute;inset:0;pointer-events:none;opacity:0;transition:opacity 0.4s ease;border-radius:inherit;background:radial-gradient(circle at 50% 50%, rgba(255,255,255,0.35), transparent 60%);z-index:2;';
        if (getComputedStyle(host).position === 'static') {
          host.style.position = 'relative';
        }
        host.appendChild(glare);
        this.glareEl = glare;
      }
    }
  }

  @HostListener('pointermove', ['$event'])
  onMove(e: PointerEvent) {
    if (!this.enabled) return;
    const host = this.el.nativeElement;
    const rect = host.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const max = this.maxDeg();

    const rotateY = (px - 0.5) * max * 2;
    const rotateX = (0.5 - py) * max * 2;

    host.style.transition = 'transform 0.08s linear';
    host.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;

    if (this.glareEl) {
      this.glareEl.style.opacity = '1';
      this.glareEl.style.background = `radial-gradient(circle at ${px * 100}% ${py * 100}%, rgba(255,255,255,0.4), transparent 55%)`;
    }
  }

  @HostListener('pointerleave')
  onLeave() {
    if (!this.enabled) return;
    const host = this.el.nativeElement;
    host.style.transition = 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)';
    host.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
    if (this.glareEl) this.glareEl.style.opacity = '0';
  }
}
